import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { Table, type Column } from '../components/ui/Table';
import type { SiteRole, SiteUserRow } from '../types';

const roleLabels: Record<SiteRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  viewer: 'Betrachter',
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      onClick={handleCopy}
      className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      {copied ? 'Kopiert ✓' : 'Kopieren'}
    </button>
  );
}

export function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();

  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<SiteRole>('viewer');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<{ email: string; password: string } | null>(null);

  const site = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => api.getSite(siteId!),
    enabled: !!siteId,
  });

  const users = useQuery({
    queryKey: ['site-users', siteId],
    queryFn: () => api.listSiteUsers(siteId!),
    enabled: !!siteId,
  });

  const invite = useMutation({
    mutationFn: () => api.inviteSiteUser(siteId!, { email, name, role }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['site-users', siteId] });
      setEmail('');
      setName('');
      setRole('viewer');
      setShowInvite(false);
      setInviteError(null);
      if (result.temporaryPassword) {
        setLastInvite({ email: result.user.email, password: result.temporaryPassword });
      }
    },
    onError: (err) => setInviteError(err instanceof ApiError ? err.message : 'Einladen fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.removeSiteUser(siteId!, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['site-users', siteId] }),
  });

  function handleInvite(e: FormEvent) {
    e.preventDefault();
    invite.mutate();
  }

  const userColumns: Column<SiteUserRow>[] = [
    { header: 'Name', cell: (r) => r.name },
    { header: 'E-Mail', cell: (r) => r.email },
    { header: 'Rolle', cell: (r) => roleLabels[r.role] },
    { header: 'Seit', cell: (r) => new Date(r.grantedAt).toLocaleDateString('de-DE') },
    {
      header: '',
      cell: (r) => (
        <button
          onClick={() => {
            if (confirm(`Zugriff für ${r.email} entziehen?`)) remove.mutate(r.id);
          }}
          className="text-xs text-rose-500 hover:underline"
        >
          Entfernen
        </button>
      ),
      align: 'right',
    },
  ];

  if (site.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!site.data) {
    return <p className="text-sm text-slate-400">Property nicht gefunden.</p>;
  }

  const snippet = `<script defer src="/datament-track/tracker.js" data-site="${site.data.site.apiKey}"></script>`;

  return (
    <div className="space-y-6">
      <Link to="/sites" className="text-sm text-slate-500 hover:underline dark:text-slate-400">
        ← Properties
      </Link>
      <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{site.data.site.name}</h1>

      <Card title="Tracking-Einbindung">
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Dieses Snippet vor dem schließenden <code>&lt;/body&gt;</code>-Tag der Kundenseite einbinden
          (Consent-Gate beachten, siehe ONBOARDING.md):
        </p>
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-slate-100 p-3 dark:bg-slate-800">
          <code className="flex-1 overflow-x-auto whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">{snippet}</code>
          <CopyButton value={snippet} />
        </div>

        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">API-Key (nur für die Domain-Anbindung, nicht öffentlich teilen):</p>
        <div className="flex items-center gap-3 rounded-lg bg-slate-100 p-3 dark:bg-slate-800">
          <code className="flex-1 overflow-x-auto whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">{site.data.site.apiKey}</code>
          <CopyButton value={site.data.site.apiKey} />
        </div>
      </Card>

      {lastInvite && (
        <Card className="border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                Zugangsdaten für {lastInvite.email} — jetzt notieren, wird nicht erneut angezeigt:
              </p>
              <p className="mt-2 font-mono text-sm text-emerald-900 dark:text-emerald-200">{lastInvite.password}</p>
            </div>
            <button
              onClick={() => setLastInvite(null)}
              className="text-xs text-emerald-700 hover:underline dark:text-emerald-400"
            >
              Ausblenden
            </button>
          </div>
        </Card>
      )}

      <Card title="Zugriff verwalten">
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setShowInvite((v) => !v)}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            {showInvite ? 'Abbrechen' : '+ Nutzer einladen'}
          </button>
        </div>

        {showInvite && (
          <form onSubmit={handleInvite} className="mb-6 flex flex-wrap items-end gap-4 border-b border-slate-100 pb-6 dark:border-slate-800">
            <div>
              <label htmlFor="invite-name" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Name
              </label>
              <input
                id="invite-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="invite-email" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                E-Mail
              </label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="invite-role" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Rolle
              </label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as SiteRole)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              >
                <option value="viewer">Betrachter</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={invite.isPending}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {invite.isPending ? 'Einladen…' : 'Einladen'}
            </button>
            {inviteError && <p className="w-full text-sm text-rose-500">{inviteError}</p>}
          </form>
        )}

        {users.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <Table columns={userColumns} rows={users.data?.users ?? []} emptyLabel="Noch keine Nutzer für diese Property." />
        )}
      </Card>
    </div>
  );
}
